/**
 * ZipCell — ZIP-variant editable cell. Renders as type="text" with a numeric +
 * dash key filter and a paste handler that strips non-zip content.
 */

import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { handleZipPaste, validateZipKey } from '../../utils/cellValidation';

import CellShell from './CellShell';
import type { BaseCellProps } from './types';
import { commitInputChange, hydrateFromValue } from './inputCellBehavior';

interface ZipCellProps extends BaseCellProps {
  maxCount?: number;
}

const ZipCell = ({
  value,
  id,
  name,
  isActive,
  isDisabled = false,
  isOptionalField = false,
  errorMessage,
  setData,
  maxCount,
}: ZipCellProps) => {
  const [inputText, setInputText] = useState(() => hydrateFromValue(value));

  useEffect(() => {
    setInputText(hydrateFromValue(value));
  }, [value]);

  const handleBlur = (inputValue: string): void => {
    commitInputChange({
      inputValue,
      originalValue: value,
      id,
      name,
      isActive,
      errorMessage,
      isOptionalField,
      setData,
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    validateZipKey(e);
  };

  return (
    <CellShell
      isActive={isActive}
      isOptionalField={isOptionalField}
      errorMessage={errorMessage}
      value={value}
    >
      <Input
        id="input-value"
        type="text"
        aria-describedby="input-value"
        name="input"
        placeholder={value === '-' ? '-' : value === null ? '-' : undefined}
        disabled={isDisabled}
        value={inputText}
        maxLength={maxCount}
        className={cn(isActive && 'border-destructive')}
        onChange={(e) => setInputText(e.target.value)}
        onBlur={(e) => handleBlur(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handleZipPaste}
        data-testid="input-value_id"
      />
    </CellShell>
  );
};

export default ZipCell;
